/**
 * MobilePlaybookDetail Component
 *
 * Mobile page for viewing and editing a playbook.
 * Route: /settings/strategies/:strategyId/playbooks/:playbookId
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, MoreVertical } from 'lucide-react';

import {
  Button,
  useDisclosure,
  Breadcrumbs,
  BreadcrumbItem,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@enfomentis/ui';
import { useStrategiesPlaybooks } from '../context';
import { ConfluenceLinkingContent, CriteriaLinkingContent, EntryModelLinkingContent, PlaybookDetailForm, PlaybookLinkageTabs, UnsavedChangesDialog, SharedIndicator, MoveWithUnsavedChangesDialog, LastPlaybookWarningDialog } from '../components';
import { PlaybookRenameModal } from '../PlaybookRenameModal';
import { StrategyPickerModal } from '../StrategyPickerModal';

export const MobilePlaybookDetail: React.FC = () => {
  const { strategyId, playbookId } = useParams<{
    strategyId: string;
    playbookId: string;
  }>();
  const navigate = useNavigate();
  const unsavedChangesDialog = useDisclosure();
  const renameModalDisclosure = useDisclosure();

  // Pending navigation target when showing unsaved changes dialog
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Track if we've validated the URL params
  const [isValidated, setIsValidated] = useState(false);

  // Actions popover state
  const [isActionsPopoverOpen, setIsActionsPopoverOpen] = useState(false);

  // Move playbook modal state
  const movePickerDisclosure = useDisclosure();
  const moveUnsavedDisclosure = useDisclosure();
  const lastPlaybookWarningDisclosure = useDisclosure();
  const [pendingMoveTargetId, setPendingMoveTargetId] = useState<string | null>(null);

  // Copy playbook modal state
  const copyPickerDisclosure = useDisclosure();

  // Share playbook modal state
  const sharePickerDisclosure = useDisclosure();

  const {
    // Strategy data
    strategies,
    loading,
    selectedStrategy,
    selectedStrategyId,
    showArchived,
    setShowArchived,
    selectStrategy,

    // Playbook data
    playbooks,
    selectedPlaybook,
    selectedPlaybookId,
    showArchivedPlaybooks,
    setShowArchivedPlaybooks,
    selectPlaybook,
    playbooksLoading,
    playbooksForStrategyId,

    // Playbook dirty state
    localPlaybookDescription,
    localPlaybookPhilosophy,
    isPlaybookDirty,
    isPlaybookSaving,
    showPlaybookConflictBanner,

    // Playbook actions
    setLocalPlaybookDescription,
    setLocalPlaybookPhilosophy,
    savePlaybook,
    discardPlaybookChanges,
    acceptPlaybookExternalChanges,
    keepPlaybookEditing,
    reloadPlaybooks,

    // Playbook sharing/management actions
    unlinkPlaybook,
    archivePlaybookFromList,
    restorePlaybookFromList,
    movePlaybook,
    copyPlaybook,
    sharePlaybook,

    // Linkage tab state
    activeLinkageTab,
    setActiveLinkageTab,

    // Playbook details collapse state
    isPlaybookDetailsExpanded,
    setIsPlaybookDetailsExpanded,
  } = useStrategiesPlaybooks();

  // Validate and sync URL params to context
  useEffect(() => {
    // Wait for strategies to load before any validation
    if (loading) {
      return;
    }

    if (!strategyId || !playbookId) {
      navigate('/settings/strategies', { replace: true });
      return;
    }

    // Validate strategy against full array
    const strategy = strategies.find((s) => s.id === strategyId);
    if (!strategy) {
      navigate('/settings/strategies', { replace: true });
      return;
    }

    // Auto-enable showArchived if strategy is archived
    if (strategy.status === 'archived' && !showArchived) {
      setShowArchived(true);
    }

    // Sync strategy selection if needed
    if (selectedStrategyId !== strategyId) {
      selectStrategy(strategyId);
      return; // Wait for playbooks to load for this strategy
    }

    // Wait for playbooks to load
    if (playbooksLoading) {
      return;
    }

    // Wait for playbooks to load for the correct strategy
    if (playbooksForStrategyId !== strategyId) {
      return;
    }

    // Additional guard: if strategy has playbooks but list is empty, wait for load
    // (reuse strategy variable from above)
    if (strategy && (strategy.playbookIds?.length ?? 0) > 0 && playbooks.length === 0) {
      return; // Wait for playbooks to load
    }

    // Validate playbook against full array
    const playbook = playbooks.find((p) => p.id === playbookId);

    if (!playbook) {
      // Playbook not found - redirect to strategy detail
      navigate(`/settings/strategies/${strategyId}`, { replace: true });
      return;
    }

    // Auto-enable showArchivedPlaybooks if playbook is archived
    if (playbook.status === 'archived' && !showArchivedPlaybooks) {
      setShowArchivedPlaybooks(true);
    }

    // Sync playbook selection if needed
    if (selectedPlaybookId !== playbookId) {
      selectPlaybook(playbookId);
    }

    setIsValidated(true);
  }, [
    strategyId,
    playbookId,
    strategies,
    playbooks,
    loading,
    selectedStrategyId,
    selectedPlaybookId,
    showArchived,
    showArchivedPlaybooks,
    playbooksLoading,
    playbooksForStrategyId,
    setShowArchived,
    setShowArchivedPlaybooks,
    selectStrategy,
    selectPlaybook,
    navigate,
  ]);

  // Get strategy name for breadcrumb
  const strategyName = useMemo(() => {
    const strategy = strategies.find((s) => s.id === strategyId);
    return strategy?.name ?? 'Strategy';
  }, [strategies, strategyId]);

  /**
   * Get strategy names that contain this playbook for SharedIndicator.
   * Only includes active, non-tombstoned strategies (same filter as desktop).
   */
  const sharedStrategyNames = useMemo(() => {
    if (!playbookId) return [];

    // Filter to active, non-tombstoned strategies that contain this playbook
    return strategies
      .filter(
        (s) =>
          s.status === 'active' &&
          !s.deletedAt &&
          s.playbookIds?.includes(playbookId)
      )
      .map((s) => s.name);
  }, [strategies, playbookId]);

  // ===========================================================================
  // Playbook Action Handlers
  // ===========================================================================

  /**
   * Handle Unlink - removes playbook from strategy, navigates back to strategy detail
   */
  const handleUnlinkClick = useCallback(async () => {
    if (!playbookId) return;
    setIsActionsPopoverOpen(false);
    await unlinkPlaybook(playbookId);
    // Navigate back to strategy detail after unlink
    navigate(`/settings/strategies/${strategyId}`);
  }, [playbookId, unlinkPlaybook, navigate, strategyId]);

  /**
   * Handle Archive - archives the playbook
   */
  const handleArchiveClick = useCallback(async () => {
    if (!playbookId) return;
    setIsActionsPopoverOpen(false);
    await archivePlaybookFromList(playbookId);
  }, [playbookId, archivePlaybookFromList]);

  /**
   * Handle Restore - restores the playbook
   */
  const handleRestoreClick = useCallback(async () => {
    if (!playbookId) return;
    setIsActionsPopoverOpen(false);
    await restorePlaybookFromList(playbookId);
  }, [playbookId, restorePlaybookFromList]);

  /**
   * Handle Rename - opens the rename modal
   */
  const handleRenameClick = useCallback(() => {
    setIsActionsPopoverOpen(false);
    renameModalDisclosure.onOpen();
  }, [renameModalDisclosure]);

  /**
   * Handle Duplicate - copies playbook within current strategy
   */
  const handleDuplicateClick = useCallback(async () => {
    if (!playbookId || !selectedStrategyId) return;
    setIsActionsPopoverOpen(false);
    await copyPlaybook(playbookId, selectedStrategyId);
  }, [playbookId, selectedStrategyId, copyPlaybook]);

  /**
   * Handle Copy to Strategy - opens strategy picker for copy
   */
  const handleCopyToStrategyClick = useCallback(() => {
    setIsActionsPopoverOpen(false);
    copyPickerDisclosure.onOpen();
  }, [copyPickerDisclosure]);

  /**
   * Handle Share with Strategy - opens strategy picker for share
   */
  const handleShareClick = useCallback(() => {
    setIsActionsPopoverOpen(false);
    sharePickerDisclosure.onOpen();
  }, [sharePickerDisclosure]);

  // ===========================================================================
  // Move Playbook Handlers
  // ===========================================================================

  /**
   * Handle Move click - if dirty, show unsaved changes dialog; else open picker
   */
  const handleMoveClick = useCallback(() => {
    setIsActionsPopoverOpen(false);
    if (isPlaybookDirty) {
      moveUnsavedDisclosure.onOpen();
    } else {
      movePickerDisclosure.onOpen();
    }
  }, [isPlaybookDirty, moveUnsavedDisclosure, movePickerDisclosure]);

  /**
   * Handle Save & Move from unsaved dialog
   */
  const handleSaveAndMove = useCallback(async () => {
    await savePlaybook();
    moveUnsavedDisclosure.onClose();
    movePickerDisclosure.onOpen();
  }, [savePlaybook, moveUnsavedDisclosure, movePickerDisclosure]);

  /**
   * Handle Discard & Move from unsaved dialog
   */
  const handleDiscardAndMove = useCallback(() => {
    discardPlaybookChanges();
    moveUnsavedDisclosure.onClose();
    movePickerDisclosure.onOpen();
  }, [discardPlaybookChanges, moveUnsavedDisclosure, movePickerDisclosure]);

  /**
   * Execute the move operation and navigate to new context
   */
  const handleExecuteMove = useCallback(
    async (targetId: string) => {
      if (!playbookId) return;
      await movePlaybook(playbookId, targetId);
      // Navigate to playbook in new strategy context
      navigate(`/settings/strategies/${targetId}/playbooks/${playbookId}`);
      // Reset state
      setPendingMoveTargetId(null);
      lastPlaybookWarningDisclosure.onClose();
    },
    [playbookId, movePlaybook, navigate, lastPlaybookWarningDisclosure]
  );

  /**
   * Handle strategy picker confirmation for move
   */
  const handleMovePickerConfirm = useCallback(
    (strategyIds: string[]) => {
      const targetId = strategyIds[0];
      if (!targetId) return;

      setPendingMoveTargetId(targetId);
      movePickerDisclosure.onClose();

      // Check if this is the last playbook in the strategy
      const strategyPlaybookCount = selectedStrategy?.playbookIds?.length ?? 0;
      if (strategyPlaybookCount === 1) {
        lastPlaybookWarningDisclosure.onOpen();
      } else {
        handleExecuteMove(targetId);
      }
    },
    [selectedStrategy, movePickerDisclosure, lastPlaybookWarningDisclosure, handleExecuteMove]
  );

  /**
   * Handle last-playbook warning confirmation
   */
  const handleLastPlaybookWarningConfirm = useCallback(() => {
    if (pendingMoveTargetId) {
      handleExecuteMove(pendingMoveTargetId);
    }
  }, [pendingMoveTargetId, handleExecuteMove]);

  /**
   * Cancel move operation
   */
  const handleMoveCancel = useCallback(() => {
    setPendingMoveTargetId(null);
    moveUnsavedDisclosure.onClose();
    movePickerDisclosure.onClose();
    lastPlaybookWarningDisclosure.onClose();
  }, [moveUnsavedDisclosure, movePickerDisclosure, lastPlaybookWarningDisclosure]);

  // ===========================================================================
  // Copy/Share Picker Handlers
  // ===========================================================================

  /**
   * Handle strategy picker confirmation for copy
   */
  const handleCopyPickerConfirm = useCallback(
    async (strategyIds: string[]) => {
      const targetId = strategyIds[0];
      if (!targetId || !playbookId) return;

      copyPickerDisclosure.onClose();
      await copyPlaybook(playbookId, targetId);

      // If copied to different strategy, navigate to new playbook
      // Note: copyPlaybook switches context and selects new playbook automatically
    },
    [playbookId, copyPlaybook, copyPickerDisclosure]
  );

  /**
   * Cancel copy operation
   */
  const handleCopyCancel = useCallback(() => {
    copyPickerDisclosure.onClose();
  }, [copyPickerDisclosure]);

  /**
   * Handle strategy picker confirmation for share
   */
  const handleSharePickerConfirm = useCallback(
    async (strategyIds: string[]) => {
      if (!playbookId || strategyIds.length === 0) return;

      sharePickerDisclosure.onClose();
      await sharePlaybook(playbookId, strategyIds);
    },
    [playbookId, sharePlaybook, sharePickerDisclosure]
  );

  /**
   * Cancel share operation
   */
  const handleShareCancel = useCallback(() => {
    sharePickerDisclosure.onClose();
  }, [sharePickerDisclosure]);

  // ===========================================================================
  // Navigation Handlers
  // ===========================================================================

  // Handle navigation to settings with dirty check
  const handleNavigateToSettings = useCallback(() => {
    if (isPlaybookDirty) {
      setPendingNavigation('/settings');
      unsavedChangesDialog.onOpen();
    } else {
      navigate('/settings');
    }
  }, [isPlaybookDirty, navigate, unsavedChangesDialog]);

  // Handle navigation to strategies list with dirty check
  const handleNavigateToStrategies = useCallback(() => {
    if (isPlaybookDirty) {
      setPendingNavigation('/settings/strategies');
      unsavedChangesDialog.onOpen();
    } else {
      navigate('/settings/strategies');
    }
  }, [isPlaybookDirty, navigate, unsavedChangesDialog]);

  // Handle navigation to strategy detail with dirty check
  const handleNavigateToStrategy = useCallback(() => {
    const strategyPath = `/settings/strategies/${strategyId}`;
    if (isPlaybookDirty) {
      setPendingNavigation(strategyPath);
      unsavedChangesDialog.onOpen();
    } else {
      navigate(strategyPath);
    }
  }, [isPlaybookDirty, strategyId, navigate, unsavedChangesDialog]);

  // Handle discard from dialog
  const handleDiscard = useCallback(() => {
    discardPlaybookChanges();
    unsavedChangesDialog.onClose();
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [discardPlaybookChanges, unsavedChangesDialog, pendingNavigation, navigate]);

  // Handle rename success - reload playbooks to get updated name
  const handleRenameSuccess = useCallback(() => {
    reloadPlaybooks();
  }, [reloadPlaybooks]);

  // Show loading while validating or loading data
  if (!isValidated || !selectedPlaybook) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  return (
    <Card radius="none" shadow="none" className="h-full flex flex-col">
      {/* Header - Breadcrumb Navigation + Page Title */}
      <CardHeader className="flex-col items-start pt-1 pb-1 border-b border-default-200">
        <div className="flex items-center">
          <Breadcrumbs variant="light" size="sm">
            <BreadcrumbItem
              onPress={handleNavigateToSettings}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Settings
            </BreadcrumbItem>
            <BreadcrumbItem
              onPress={handleNavigateToStrategies}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Strategies
            </BreadcrumbItem>
            <BreadcrumbItem
              onPress={handleNavigateToStrategy}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              {strategyName}
            </BreadcrumbItem>
          </Breadcrumbs>
        </div>
        <div className="flex items-center justify-between w-full mt-1">
          <h1 className="text-lg font-semibold text-text-primary min-w-0">{selectedPlaybook.name}</h1>
          <div className="flex items-center gap-2 shrink-0">
            {sharedStrategyNames.length >= 2 && (
              <SharedIndicator strategyNames={sharedStrategyNames} />
            )}
            {selectedPlaybook.status === 'archived' && (
              <span className="text-xs text-text-tertiary">Archived</span>
            )}
            {/* Actions Popover */}
            <Popover
              isOpen={isActionsPopoverOpen}
              onOpenChange={setIsActionsPopoverOpen}
              placement="bottom-end"
            >
              <PopoverTrigger>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  aria-label="Playbook actions"
                  className="opacity-60 hover:opacity-100"
                >
                  <MoreVertical size={16} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-1">
                <div className="flex flex-col min-w-40">
                  <Button
                    size="md"
                    variant="light"
                    className="justify-start"
                    isDisabled={isPlaybookSaving}
                    onPress={handleRenameClick}
                  >
                    Rename
                  </Button>
                  <Button
                    size="md"
                    variant="light"
                    className="justify-start"
                    isDisabled={isPlaybookSaving}
                    onPress={handleDuplicateClick}
                  >
                    Duplicate
                  </Button>
                  <Button
                    size="md"
                    variant="light"
                    className="justify-start"
                    isDisabled={isPlaybookSaving}
                    onPress={handleCopyToStrategyClick}
                  >
                    Copy to Strategy...
                  </Button>
                  <Button
                    size="md"
                    variant="light"
                    className="justify-start"
                    isDisabled={isPlaybookSaving}
                    onPress={handleMoveClick}
                  >
                    Move to Strategy...
                  </Button>
                  <Button
                    size="md"
                    variant="light"
                    className="justify-start"
                    isDisabled={isPlaybookSaving}
                    onPress={handleShareClick}
                  >
                    Share with Strategy...
                  </Button>
                  <Button
                    size="md"
                    variant="light"
                    className="justify-start"
                    isDisabled={isPlaybookSaving}
                    onPress={handleUnlinkClick}
                  >
                    Unlink from Strategy
                  </Button>
                  {selectedPlaybook.status !== 'archived' && (
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start text-warning-600"
                      isDisabled={isPlaybookSaving}
                      onPress={handleArchiveClick}
                    >
                      Archive
                    </Button>
                  )}
                  {selectedPlaybook.status === 'archived' && (
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start text-success-600"
                      isDisabled={isPlaybookSaving}
                      onPress={handleRestoreClick}
                    >
                      Restore
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </CardHeader>

      {/* Content */}
      <CardBody className="flex-1 min-h-0 flex flex-col px-2 pb-2 gap-3">
        {/* Conflict Banner */}
        {showPlaybookConflictBanner && (
          <div className="rounded-lg bg-warning-50 border border-warning-200 p-3 shrink-0">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-warning-600 shrink-0" />
              <span className="text-sm text-warning-700">Updated in another tab</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="flat" onPress={keepPlaybookEditing}>
                Keep editing
              </Button>
              <Button size="sm" color="warning" onPress={acceptPlaybookExternalChanges}>
                Accept changes
              </Button>
            </div>
          </div>
        )}

        {/* Collapsible Playbook Details Card */}
        <Card shadow="none" radius="sm" className="border border-default-200 shrink-0">
          <CardHeader className="flex items-center justify-between px-3 py-2">
            <span className="text-sm text-text-primary">Description & Philosophy</span>
            <Button
              size="sm"
              variant="light"
              onPress={() => setIsPlaybookDetailsExpanded(!isPlaybookDetailsExpanded)}
            >
              {isPlaybookDetailsExpanded ? 'Hide Details' : 'Show Details'}
            </Button>
          </CardHeader>
          {isPlaybookDetailsExpanded && (
            <CardBody className="border-t border-default-200 p-3">
              <PlaybookDetailForm
                description={localPlaybookDescription}
                philosophy={localPlaybookPhilosophy}
                onDescriptionChange={setLocalPlaybookDescription}
                onPhilosophyChange={setLocalPlaybookPhilosophy}
              />
            </CardBody>
          )}
        </Card>

        {/* Playbook Linkage Tabs */}
        <PlaybookLinkageTabs
          activeTab={activeLinkageTab}
          onTabChange={setActiveLinkageTab}
          criteriaContent={<CriteriaLinkingContent />}
          confluencesContent={<ConfluenceLinkingContent />}
          entryModelsContent={<EntryModelLinkingContent />}
        />
      </CardBody>

      {/* Footer - Management actions (Unlink/Archive/Restore) moved to list page popover */}
      <CardFooter className="flex items-center justify-end gap-2 pt-2 border-t border-default-200">
        {isPlaybookDirty && <span className="text-xs text-warning-600">Unsaved</span>}
        <Button
          variant="flat"
          size="sm"
          onPress={discardPlaybookChanges}
          isDisabled={!isPlaybookDirty || isPlaybookSaving}
        >
          Discard
        </Button>
        <Button
          color="primary"
          size="sm"
          onPress={savePlaybook}
          isDisabled={!isPlaybookDirty || isPlaybookSaving}
          isLoading={isPlaybookSaving}
        >
          Save
        </Button>
      </CardFooter>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        isOpen={unsavedChangesDialog.isOpen}
        onClose={() => {
          unsavedChangesDialog.onClose();
          setPendingNavigation(null);
        }}
        onDiscard={handleDiscard}
      />

      {/* Rename Modal */}
      {selectedPlaybook && (
        <PlaybookRenameModal
          isOpen={renameModalDisclosure.isOpen}
          onClose={renameModalDisclosure.onClose}
          playbook={selectedPlaybook}
          onSave={handleRenameSuccess}
        />
      )}

      {/* Move Playbook Modals */}
      <MoveWithUnsavedChangesDialog
        isOpen={moveUnsavedDisclosure.isOpen}
        onClose={handleMoveCancel}
        onSaveAndMove={handleSaveAndMove}
        onDiscardAndMove={handleDiscardAndMove}
        isSaving={isPlaybookSaving}
      />

      {selectedStrategy && playbookId && (
        <StrategyPickerModal
          isOpen={movePickerDisclosure.isOpen}
          onClose={handleMoveCancel}
          onConfirm={handleMovePickerConfirm}
          mode="move"
          playbookId={playbookId}
          currentStrategyId={selectedStrategy.id}
          playbookName={selectedPlaybook?.name || 'Playbook'}
        />
      )}

      {selectedStrategy && (
        <LastPlaybookWarningDialog
          isOpen={lastPlaybookWarningDisclosure.isOpen}
          onClose={handleMoveCancel}
          onConfirm={handleLastPlaybookWarningConfirm}
          strategyName={selectedStrategy.name}
        />
      )}

      {/* Copy Playbook Modal */}
      {selectedStrategy && playbookId && (
        <StrategyPickerModal
          isOpen={copyPickerDisclosure.isOpen}
          onClose={handleCopyCancel}
          onConfirm={handleCopyPickerConfirm}
          mode="copy"
          playbookId={playbookId}
          currentStrategyId={selectedStrategy.id}
          playbookName={selectedPlaybook?.name || 'Playbook'}
        />
      )}

      {/* Share Playbook Modal */}
      {selectedStrategy && playbookId && (
        <StrategyPickerModal
          isOpen={sharePickerDisclosure.isOpen}
          onClose={handleShareCancel}
          onConfirm={handleSharePickerConfirm}
          mode="share"
          playbookId={playbookId}
          currentStrategyId={selectedStrategy.id}
          playbookName={selectedPlaybook?.name || 'Playbook'}
        />
      )}
    </Card>
  );
};
